<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserSettingsStore } from '@/stores/userSettings'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { USER_SETTING } from '@/services/apiModules'
import { useUserStore } from '@/stores/userStore'
import { useApiMutation } from '@/composables/useApiMutation'

defineProps<{
  isTopRight?: boolean
}>()

const { t, locale } = useI18n()
const settingsStore = useUserSettingsStore()
const userStore = useUserStore()

const language = computed(() => settingsStore.language)

function switchTo(language: 'en' | 'de') {
  settingsStore.updateSettings({ language })
  locale.value = language
  if (userStore.userID) {
    updateSettings()
  }
}

const { mutateAsync, isPending } = useApiMutation({
  modus: USER_SETTING,
})

async function updateSettings() {
  try {
    await mutateAsync({
      payload: {
        settings: {
          language: settingsStore.language,
          theme: settingsStore.theme,
        },
      },
      method: 'PUT',
    })
  } catch (error) {
    console.error('Error updating settings:', error)
  } finally {
  }
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as="div">
      <TooltipProvider>
        <Tooltip disableClosingTrigger>
          <TooltipTrigger
            class="focus:ring-accent-700 hover:text-sidebar-foreground cursor-pointer rounded-lg transition focus:ring-2 focus:outline-none"
          >
            <button
              type="button"
              :aria-label="t('nav.labels.language')"
              class="focus:ring-accent-700 hover:text-sidebar-foreground text-tertiary cursor-pointer rounded-lg p-2 transition focus:ring-2 focus:outline-none"
            >
              <Icon icon="fa:language-regular" class="cursor-pointer text-xl lg:text-2xl" />
            </button>
          </TooltipTrigger>

          <TooltipContent :side="isTopRight ? 'bottom' : 'right'" :sideOffset="8">
            <p>{{ t('nav.labels.language') }}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </DropdownMenuTrigger>

    <DropdownMenuContent :side="isTopRight ? 'bottom' : 'right'" class="ml-3">
      <DropdownMenuItem
        :disabled="isPending"
        @click="switchTo('en')"
        :class="[
          {
            'text-primary focus:text-primary dark:text-tertiary bg-gray-100 font-semibold':
              language === 'en',
          },
          'cursor-pointer',
        ]"
      >
        <svg
          enable-background="new 0 0 512 512"
          viewBox="0 0 512 512"
          xmlns="http://www.w3.org/2000/svg"
          class="h-12 w-12"
        >
          <!-- From https://svgflags.com -->
          <circle cx="256" cy="256" fill="#f0f0f0" r="256" />
          <g fill="#0052b4">
            <path d="m52.92 100.142c-20.109 26.163-35.272 56.318-44.101 89.077h133.178z" />
            <path d="m503.181 189.219c-8.829-32.758-23.993-62.913-44.101-89.076l-89.075 89.076z" />
            <path d="m8.819 322.784c8.83 32.758 23.993 62.913 44.101 89.075l89.074-89.075z" />
            <path d="m411.858 52.921c-26.163-20.109-56.317-35.272-89.076-44.102v133.177z" />
            <path d="m100.142 459.079c26.163 20.109 56.318 35.272 89.076 44.102v-133.176z" />
            <path d="m189.217 8.819c-32.758 8.83-62.913 23.993-89.075 44.101l89.075 89.075z" />
            <path d="m322.783 503.181c32.758-8.83 62.913-23.993 89.075-44.101l-89.075-89.075z" />
            <path d="m370.005 322.784 89.075 89.076c20.108-26.162 35.272-56.318 44.101-89.076z" />
          </g>
          <g fill="#d80027">
            <path
              d="m509.833 222.609h-220.44-.001v-220.442c-10.931-1.423-22.075-2.167-33.392-2.167-11.319 0-22.461.744-33.391 2.167v220.44.001h-220.442c-1.423 10.931-2.167 22.075-2.167 33.392 0 11.319.744 22.461 2.167 33.391h220.44.001v220.442c10.931 1.423 22.073 2.167 33.392 2.167 11.317 0 22.461-.743 33.391-2.167v-220.44-.001h220.442c1.423-10.931 2.167-22.073 2.167-33.392 0-11.317-.744-22.461-2.167-33.391z"
            />
            <path
              d="m322.783 322.784 114.236 114.236c5.254-5.252 10.266-10.743 15.048-16.435l-97.802-97.802h-31.482z"
            />
            <path
              d="m189.217 322.784h-.002l-114.235 114.235c5.252 5.254 10.743 10.266 16.435 15.048l97.802-97.804z"
            />
            <path
              d="m189.217 189.219v-.002l-114.236-114.237c-5.254 5.252-10.266 10.743-15.048 16.435l97.803 97.803h31.481z"
            />
            <path
              d="m322.783 189.219 114.237-114.238c-5.252-5.254-10.743-10.266-16.435-15.047l-97.802 97.803z"
            />
          </g>
        </svg>
        English
      </DropdownMenuItem>
      <DropdownMenuItem
        @click="switchTo('de')"
        :class="[
          {
            'text-primary focus:text-primary dark:text-tertiary bg-gray-100 font-semibold':
              language === 'de',
          },
          'cursor-pointer',
        ]"
      >
        <svg
          enable-background="new 0 0 512 512"
          viewBox="0 0 512 512"
          xmlns="http://www.w3.org/2000/svg"
          class="h-12 w-12"
        >
          <!-- From https://svgflags.com -->
          <path
            d="m15.923 345.043c36.171 97.484 130.006 166.957 240.077 166.957s203.906-69.473 240.077-166.957l-240.077-22.26z"
            fill="#ffda44"
          />
          <path
            d="m256 0c-110.071 0-203.906 69.472-240.077 166.957l240.077 22.26 240.077-22.261c-36.171-97.484-130.006-166.956-240.077-166.956z"
          />
          <path
            d="m15.923 166.957c-10.29 27.733-15.923 57.729-15.923 89.043s5.633 61.31 15.923 89.043h480.155c10.29-27.733 15.922-57.729 15.922-89.043s-5.632-61.31-15.923-89.043z"
            fill="#d80027"
          />
        </svg>
        Deutsch
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
